import { FC, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Divider,
    Tooltip,
    Typography,
} from '@mui/material';
import { getEstimatedTankLevel } from 'store/services/device-predictions';
import { StoredUser } from 'store/features/authentication';
import { Device } from 'types';
import { formatNumberAsString, toTitleCase } from 'helpers';
import { getDateWithTime } from 'helpers/dates';
import { MapWithMarker } from 'components/data/MapWithMarker/MapWithMarker';
import DeviceHealthIcon from 'components/Devices/DeviceHealthIcon/DeviceHealthIcon';
import DeviceStatus from 'components/Devices/DeviceStatus/DeviceStatus';
import DeviceSignalIcon from 'components/Devices/DeviceSignalIcon/DeviceSignalIcon';
import DeviceBatteryIcon from 'components/Devices/DeviceBatteryIcon/DeviceBatteryIcon';
import { LastReadingTimeIcon } from 'components/Devices/LastReadingTimeIcon/LastReadingTimeIcon';
import { ReportIssueModal } from 'views/DeviceDetails/modals/ReportIssueModal';
import styles from './DeviceInformationMainCard.module.scss';
import { useNavigate } from 'react-router-dom';
import {
    CopyAll,
    Edit,
    HelpOutline,
    Info,
    ReportProblem,
} from '@mui/icons-material';
import {
    CollapsibleFab,
    CollapsibleFabButtonItem,
} from 'components/button/CollapsibleFab/CollapsibleFab';
import DaysUntilRecommendedFill from 'components/data/DaysUntilRecommendedFill/DaysUntilRecommendedFill';
import { DeviceFuelTypeIcon } from 'components/Devices/DeviceFuelTypeIcon/DeviceFuelTypeIcon';
import { TankLevelIndicator } from 'components/data/TankLevelIndicator/TankLevelIndicator';
import { useSnackbar } from 'hooks/useSnackbar';

interface DeviceInformationMainCardProps {
    device: Device;
    user: StoredUser | null;
    onClickViewDetails?: () => void;
}

const DeviceInformationMainCard: FC<DeviceInformationMainCardProps> = ({
    device,
    user,
    onClickViewDetails,
}) => {
    const navigate = useNavigate();

    const navigateToConfig = () => {
        navigate(`/device-details/${device.deviceId}/config`);
    };

    const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);

    const activateSnackbar = useSnackbar();

    let navButtons: CollapsibleFabButtonItem[] = [
        {
            text: 'Edit',
            icon: <Edit />,
            onClick: navigateToConfig,
            id: 'EditButton',
        },
        {
            text: 'Report a Problem',
            icon: <ReportProblem />,
            onClick: () => setIsReportModalOpen(true),
            color: 'error',
        },
        {
            text: `Copy ID: ${device.deviceId}`,
            icon: <CopyAll />,
            onClick: () => {
                navigator.clipboard.writeText(device.deviceId);
                activateSnackbar({
                    message: 'Device ID copied to clipboard.',
                });
            },
            color: 'info',
        },
    ];

    navButtons = onClickViewDetails
        ? [
              {
                  text: 'View Details',
                  icon: <Info />,
                  onClick: onClickViewDetails,
              },
              ...navButtons,
          ]
        : navButtons;

    return (
        <div
            className={styles.DeviceInformationMainCard}
            data-testid="DeviceInformationMainCard"
        >
            <Card>
                <CardContent className={styles.cardContent}>
                    {/* Heading */}
                    <div className={styles.column}>
                        <MapWithMarker
                            address={device.address}
                            customHeight="230px"
                        />

                        <div className={styles.titleContainer}>
                            <div
                                className={styles.row}
                                style={{ flexWrap: 'wrap' }}
                            >
                                <span className={styles.DeviceHealthIcon}>
                                    <DeviceHealthIcon
                                        color={device.health?.color}
                                        icon={device.health?.icon}
                                        tooltip={device.health?.tooltip}
                                    />
                                </span>
                                <Typography
                                    variant="h5"
                                    data-testid="DeviceName"
                                >
                                    {device.name || device.shortDeviceId}
                                </Typography>
                            </div>
                            <div className={styles.row}>
                                <Typography
                                    data-testid="ProductId"
                                    className={styles.monospaced}
                                >
                                    {!!device.product_id
                                        ? device.product_id.toUpperCase()
                                        : 'No Product ID'}
                                </Typography>
                                <span className={styles.DeviceStatus}>
                                    <DeviceStatus
                                        status={toTitleCase(device.status)}
                                    ></DeviceStatus>
                                </span>
                                <span>
                                    <DeviceSignalIcon
                                        {...device}
                                    ></DeviceSignalIcon>
                                </span>

                                {device.virtual ? null : (
                                    <span>
                                        <DeviceBatteryIcon
                                            battery={device.lastReading}
                                            itemNumber={device.item_number}
                                        />
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Subheading */}
                        <div
                            className={styles.row}
                            style={{ justifyContent: 'space-between' }}
                        >
                            <div
                                className={`${styles.column}`}
                                style={{ flexGrow: 1 }}
                            >
                                <Typography
                                    data-testid="CustomerName"
                                    color="text.secondary"
                                    variant="subtitle1"
                                >
                                    {device.customer_name}
                                </Typography>
                                <Typography
                                    data-testid="DeviceAddress"
                                    color="text.secondary"
                                    variant="subtitle1"
                                    maxWidth="75%"
                                >
                                    {device.address || 'No Address Entered'}
                                </Typography>
                            </div>
                            <div className={styles.rightSideInfo}>
                                <Tooltip title="Short Device ID">
                                    <Typography
                                        data-testid="DeviceShortId"
                                        color="text.secondary"
                                        className={styles.monospaced}
                                        textAlign="end"
                                    >
                                        {device.shortDeviceId}
                                    </Typography>
                                </Tooltip>
                                <div
                                    className={styles.fuelType}
                                    data-testid="FuelType"
                                >
                                    <Typography
                                        color="text.secondary"
                                        data-testid="TankCapacity"
                                    >
                                        {formatNumberAsString(
                                            device.capacity ?? 0
                                        )}{' '}
                                        gallons
                                    </Typography>
                                    <DeviceFuelTypeIcon
                                        fuelType={device.fuelType}
                                        orientation={device.orientation}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <Divider textAlign="left">
                        <Typography variant="overline" color="text.secondary">
                            Tank Level
                        </Typography>
                    </Divider>

                    {/* Tank Readings */}
                    <div className={`${styles.column}`}>
                        <div
                            className={`${styles.rowSpacedOut} ${styles.field}`}
                        >
                            <div>
                                <Typography
                                    className={`${styles.fieldLabel}`}
                                    color="text.secondary"
                                    data-testid="EstimatedTankLevelLabel"
                                >
                                    Estimated Tank Level
                                    <Tooltip
                                        title="Tank level estimated based on prediction algorithms."
                                        enterTouchDelay={0}
                                        arrow
                                    >
                                        <HelpOutline
                                            color="action"
                                            fontSize="small"
                                        />
                                    </Tooltip>
                                </Typography>
                                {device.duf_7 !== undefined && (
                                    <Box
                                        className={styles.daysUntilFill}
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        Est.{' '}
                                        <span>
                                            <DaysUntilRecommendedFill
                                                duf_7={device.duf_7}
                                            />{' '}
                                        </span>
                                        Days Until Next Fill
                                    </Box>
                                )}
                            </div>
                            <span
                                data-testid="EstimatedTankLevel"
                                className={styles.levelChart}
                            >
                                <TankLevelIndicator
                                    tankLevelPercentage={getEstimatedTankLevel(
                                        device.lastReading,
                                        device.lastPrediction
                                    )}
                                    gallons={device.gallons}
                                />
                            </span>
                        </div>
                        <div
                            className={`${styles.rowSpacedOut} ${styles.field}`}
                        >
                            <div>
                                <Typography
                                    className={`${styles.fieldLabel}`}
                                    color="text.secondary"
                                    data-testid="LastReadingTankLevelLabel"
                                >
                                    Last Reading Tank Level
                                    <Tooltip
                                        title="Tank level based on the most recent monitor reading."
                                        enterTouchDelay={0}
                                        arrow
                                    >
                                        <HelpOutline
                                            color="action"
                                            fontSize="small"
                                        />
                                    </Tooltip>
                                </Typography>
                                <Box
                                    data-testid="LastReadingTime"
                                    sx={{
                                        color: 'text.secondary',
                                        fontSize: 'small',
                                    }}
                                >
                                    As of{' '}
                                    {getDateWithTime(
                                        device.lastReading?.time as number
                                    )}
                                    <span className={styles.lastReadingIcon}>
                                        {' '}
                                        <LastReadingTimeIcon
                                            {...device}
                                        ></LastReadingTimeIcon>
                                    </span>
                                </Box>
                            </div>
                            <span data-testid="LastReadingTankLevel">
                                <TankLevelIndicator
                                    tankLevelPercentage={
                                        device.lastReading?.tank || 0
                                    }
                                    gallons={device.gallons}
                                />
                            </span>
                        </div>
                    </div>
                </CardContent>
                <CollapsibleFab buttons={navButtons} />
            </Card>
            <ReportIssueModal
                device={device}
                modalOpen={isReportModalOpen}
                setModalOpen={setIsReportModalOpen}
                user={user}
            />
        </div>
    );
};

export default DeviceInformationMainCard;
